import { HttpBackend, HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import { Router } from '@angular/router'

import { BehaviorSubject, Observable, of, throwError } from 'rxjs'
import { catchError, map, tap } from 'rxjs/operators'

import { environment } from '../../../../environments/environment'
import { DataResponse } from '../../../shared/library/auth/data-response.interface'
import { parseAccountRole } from '../../../shared/library/enum/account-role.enum'
import { LoggedUser } from '../../../shared/library/model/logged-user.model'

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly TOKEN_KEY = 'access_token'
  private readonly CSRF_TOKEN_KEY = 'csrf_token'
  private http = inject(HttpClient)
  private httpBackend = inject(HttpBackend)
  private router = inject(Router)

  private authorizationDataSource = new BehaviorSubject<DataResponse | null>(null)
  authorizationData$ = this.authorizationDataSource.asObservable()

  /**
   * Perform login using a client without interceptors, store access token,
   * and capture CSRF token from response headers for double-submit.
   */
  login(credentials: any): Observable<DataResponse> {
    const httpWithoutInterceptors = new HttpClient(this.httpBackend)

    const headers = new HttpHeaders({
      'X-API-Key': environment.xApiKey,
      'Content-Type': 'application/json',
    })

    return httpWithoutInterceptors
      .post<DataResponse>(`${environment.apiBase}auth/authentication`, credentials, {
        headers,
        withCredentials: true,
        observe: 'response',
      })
      .pipe(
        map((resp: HttpResponse<DataResponse>) => {
          const loginResponse = resp.body as DataResponse
          const csrfHeader = resp.headers.get('X-CSRF-Token')
          if (csrfHeader) {
            this.setCsrfToken(csrfHeader)
          }
          if (loginResponse && loginResponse.data && loginResponse.data.access_token) {
            this.setToken(loginResponse.data.access_token)
          }
          return loginResponse
        }),
      )
  }

  setToken(token: string): void {
    localStorage.setItem(this.TOKEN_KEY, token)
  }

  getToken(): string | null {
    try {
      return localStorage.getItem(this.TOKEN_KEY)
    } catch (error) {
      console.error('Error accessing localStorage:', error)
      return null
    }
  }

  /**
   * Decode a JWT payload safely (base64url) and return object or null.
   */
  private decodeJwtPayload(token: string): Record<string, any> | null {
    try {
      const parts = token.split('.')
      if (parts.length !== 3) return null
      const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/')
      const json = atob(payload)
      return JSON.parse(json)
    } catch (_) {
      return null
    }
  }

  /**
   * Check if the current token is expired considering configured leeway seconds.
   */
  isTokenExpired(): boolean {
    const token = this.getToken()
    if (!token) return true
    const payload = this.decodeJwtPayload(token)
    const exp = payload?.['exp'] as number | undefined
    if (!exp || typeof exp !== 'number') {
      // If no exp claim, consider token invalid/expired
      return true
    }
    const nowSec = Math.floor(Date.now() / 1000)
    return nowSec >= exp - (environment.tokenLeewaySeconds ?? 0)
  }

  isLoggedIn(): boolean {
    try {
      const token = this.getToken()
      if (!token) return false
      return !this.isTokenExpired()
    } catch (error) {
      console.error('Error checking login status:', error)
      return false
    }
  }

  /**
   * Execute server-side logout with CSRF header and clear local session state.
   */
  logout(reason?: string): Observable<void> {
    const csrf = this.getCsrfToken()
    const headers = new HttpHeaders({ 'X-CSRF-Token': csrf ?? '' })
    return this.http
      .post<void>(`${environment.apiBase}auth/logout`, {}, { headers, withCredentials: true })
      .pipe(
        tap(() => {
          this.clearSession()
          this.authorizationDataSource.next(null)
          // Navegar para a página de login com possível razão
          const queryParams = reason ? { reason } : undefined
          this.router.navigate(['/auth'], { queryParams })
        }),
        catchError((err) => {
          // Even on error, clear session locally to ensure logout
          this.clearSession()
          this.authorizationDataSource.next(null)
          const queryParams = reason ? { reason } : undefined
          this.router.navigate(['/auth'], { queryParams })
          return throwError(() => err)
        }),
      )
  }

  getAuthorizationData(): Observable<DataResponse> {
    return this.http
      .get<DataResponse>(`${environment.apiBase}auth/authorization`)
      .pipe(tap((data) => this.authorizationDataSource.next(data)))
  }

  getLoggedUser(): Observable<LoggedUser | null> {
    return this.getAuthorizationData().pipe(
      map((authorizationResponse): LoggedUser | null => {
        const data = authorizationResponse?.data

        // Retorna null se não há dados de autorização
        if (!data) {
          return null
        }

        const accountRole = parseAccountRole(data.user.role)
        return new LoggedUser({
          name: (data.user.name ?? '') as string,
          email: (data.user.email ?? '') as string,
          role: [accountRole],
        })
      }),
      catchError((error) => {
        console.error('Erro ao buscar usuário logado:', error)
        return of(null)
      }),
    )
  }

  setCsrfToken(token: string): void {
    try {
      sessionStorage.setItem(this.CSRF_TOKEN_KEY, token)
    } catch (error) {
      console.error('Error accessing sessionStorage:', error)
    }
  }

  getCsrfToken(): string | null {
    try {
      return sessionStorage.getItem(this.CSRF_TOKEN_KEY)
    } catch (error) {
      console.error('Error accessing sessionStorage:', error)
      return null
    }
  }

  clearSession(): void {
    try {
      localStorage.removeItem(this.TOKEN_KEY)
      sessionStorage.removeItem(this.CSRF_TOKEN_KEY)
    } catch (error) {
      console.error('Error clearing session storage:', error)
    }
  }

  /**
   * Update current user's password.
   * Uses CSRF token and credentials for secure submission.
   * Adjust endpoint if backend differs.
   */
  updatePassword(userId: string | number, newPassword: string): Observable<DataResponse> {
    const csrf = this.getCsrfToken()
    const headers = new HttpHeaders({
      'X-CSRF-Token': csrf ?? '',
      'X-API-Key': environment.xApiKey,
      'Content-Type': 'application/json',
    })
    return this.http.put<DataResponse>(
      `${environment.apiBase}admin/users/${userId}/password`,
      { password: newPassword },
      { headers, withCredentials: true },
    )
  }
}
